import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Chip, SxProps, Theme } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { EditFormDetails } from "../../interfaces";
import { Module } from "../../../services/Module";
import {
  BusinessRulePerimeterAdd,
  DimensionSelected,
} from "./BusinessRulePerimeterAdd";
import { ActionTooltip } from "../../../components/Tooltips";

const useStyles = makeStyles()((theme: Theme) => ({
  greyText: {
    color: theme.vars.palette.text.secondary,
    fontSize: "small",
    fontWeight: "bold",
  },
}));

/**
 * Dimension list item component.
 */
const DimensionListItem = ({
  dimensionWithValue,
  onDelete,
  sx,
}: {
  dimensionWithValue: DimensionSelected;
  onDelete: (id: number) => void;
  moduleSlug: string;
  sx: SxProps<Theme>;
}): JSX.Element => {
  /**
   * Format the tooltip title.
   */
  const tooltipTitle = (dimensionValues: DimensionSelected["dimensionValue"]) =>
    dimensionValues.map((dimensionValue) => dimensionValue.id).join(", ");

  return (
    <>
      <ActionTooltip
        title={tooltipTitle(dimensionWithValue.dimensionValue)}
        translateTitle={false}
        rawChildren
      >
        <Chip
          label={
            dimensionWithValue.dimension.label +
            ` (${dimensionWithValue.dimensionValue.length})`
          }
          onDelete={() => onDelete(dimensionWithValue.dimension.id)}
          sx={sx}
        />
      </ActionTooltip>
    </>
  );
};

interface ValueFormatted {
  dimension_attribute: {
    id: number;
  };
  values: number[];
}

export interface Value {
  dimension_attribute: {
    id: number;
    dimension: {
      id: number;
      label: string;
      order: number;
      granularity: number;
    };
    label: string;
  };
  values: number[];
}

/**
 * BusinessRuleParameter form component.
 */
export const BusinessRuleParameter = (params: {
  filter: EditFormDetails;
  name?: string;
  helpers: any;
  value: Value[] | null;
}) => {
  const { filter, helpers, value } = params;

  /**
   * Formats "value" to "DimensionSelected" type.
   */
  const getInitialValue = (value: Value[] | null): DimensionSelected[] => {
    if (!value) {
      return [];
    }

    return value.map((val) => ({
      dimension: {
        id: val.dimension_attribute.id,
        label: val.dimension_attribute.label,
      },
      dimensionValue: val.values.map((value) => ({
        id: value,
      })),
    }));
  };

  const moduleSlug = Module.ALERT;

  const { classes } = useStyles();
  const [parameters, setParameters] = useState<DimensionSelected[]>(
    getInitialValue(value)
  );

  /**
   * Structure object for helper value (data sent to API)
   */
  const setValueFormater = (
    parameters: DimensionSelected[]
  ): ValueFormatted[] => {
    const acc: ValueFormatted[] = [];

    for (const parameter of parameters) {
      if (!parameter.dimension.id) {
        continue;
      }

      acc.push({
        dimension_attribute: {
          id: parameter.dimension.id,
        },
        values: parameter.dimensionValue.map(
          (dimensionValue) => dimensionValue.id
        ),
      });
    }

    return acc;
  };

  /**
   * Removes from dimension from its id.
   */
  const onDeleteListItem = (id: number): void => {
    const parametersToSet = parameters.filter(
      (parameter) => parameter.dimension.id !== id
    );

    setParameters(parametersToSet);
    helpers.setValue(setValueFormater(parametersToSet));
  };

  /**
   * Add a new dimension to the list.
   * Check if there is a duplicate dimension.id and remove previous one.
   */
  const onChangePerimeterAdd = useCallback(
    (params: DimensionSelected): void => {
      const newParameters = parameters.filter(
        (parameter) => parameter.dimension.id !== params.dimension.id
      );

      const parametersToSet = [...newParameters, params];

      setParameters(parametersToSet);
      helpers.setValue(setValueFormater(parametersToSet));
    },
    [parameters, helpers]
  );

  useEffect(() => {
    if (!value) {
      helpers.setValue([]);
    }
  }, [helpers, value]);

  return (
    <>
      <span className={classes.greyText}>
        <FormattedMessage id={filter.label} />
      </span>
      <Box>
        <>
          {parameters.map((parameter, index) => (
            <DimensionListItem
              sx={{ marginRight: 1 }}
              key={index}
              dimensionWithValue={parameter}
              onDelete={onDeleteListItem}
              moduleSlug={moduleSlug}
            />
          ))}
          <BusinessRulePerimeterAdd
            onSubmit={onChangePerimeterAdd}
            moduleSlug={moduleSlug}
          />
        </>
      </Box>
    </>
  );
};
